import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errText, downloadFile, CT_BG, bandColor } from "@/lib/api";
import { Shell, CTGrid, StatCard } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Users } from "lucide-react";

const fmtDuration = (secs) => {
  if (secs === null || secs === undefined) return "N/A";
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return h ? `${h}h ${m}m ${s}s` : m ? `${m}m ${s}s` : `${s}s`;
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "N/A");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }) : "N/A");

export default function TeacherDashboard() {
  const [overview, setOverview] = useState(null);
  const [selClass, setSelClass] = useState(null);
  const [division, setDivision] = useState(null);
  const [tests, setTests] = useState([]);
  const [testId, setTestId] = useState("");
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/teacher/overview").then((r) => setOverview(r.data)); }, []);

  const openDivision = async (cls, div, tid = "") => {
    setSelClass(cls); setDivision(div); setTestId(tid);
    const params = { class_id: cls.id, division: div.name };
    if (tid) params.test_id = tid;
    const { data } = await api.get("/analytics/division", { params });
    setResult(data);
    if (!tests.length || selClass?.id !== cls.id) {
      const subs = await api.get("/subjects", { params: { class_id: cls.id } });
      const all = [];
      for (const s of subs.data) {
        const chs = await api.get("/chapters", { params: { class_id: cls.id, subject_id: s.id } });
        for (const ch of chs.data) {
          const ts = await api.get("/tests", { params: { chapter_id: ch.id } });
          ts.data.forEach((t) => all.push({ ...t, label: `${s.name} · ${ch.name} · ${t.title}` }));
        }
      }
      setTests(all);
    }
  };

  const download = async (fmt) => {
    try {
      await downloadFile(
        `/reports/division.${fmt}?class_id=${selClass.id}&division=${division.name}${testId ? `&test_id=${testId}` : ""}`,
        `${selClass.name}-${division.name}-results.${fmt}`
      );
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(errText(e)); }
  };

  if (!selClass || !division) {
    return (
      <Shell title={overview?.school?.name || "School Dashboard"} subtitle="Select a class to view results" nav={
        <Link to="/teacher/students"><Button variant="ghost" size="sm" data-testid="nav-manage-students"><Users className="h-4 w-4 mr-1" />Manage Students</Button></Link>
      }>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="teacher-stats">
          <StatCard label="Students" value={overview?.stats?.total_students ?? 0} testid="stat-total-students" />
          <StatCard label="Tests Completed" value={overview?.stats?.total_attempts ?? 0} testid="stat-attempts" />
          <StatCard label="Average Score" value={`${overview?.stats?.avg_percentage ?? 0}%`} testid="stat-avg" />
          <StatCard label="School ID" value={overview?.school?.school_code || "—"} testid="stat-code" />
        </div>

        <h3 className="text-xl font-head font-semibold mt-10 mb-4">School CT Competency Overview</h3>
        <CTGrid ct={overview?.stats?.ct} testid="school-ct-grid" />

        <h3 className="text-xl font-head font-semibold mt-10 mb-4">Classes</h3>
        {overview && overview.classes.length === 0 && <p className="text-slate-500">No classes/students assigned to your school yet.</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="class-cards">
          {(overview?.classes || []).map((c) => (
            <div key={c.id} className="soft-card soft-card-hover p-6" data-testid={`class-card-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <h4 className="text-xl font-head font-bold">{c.name}</h4>
              <p className="text-sm text-slate-500 mt-1">{c.student_count} students · {c.divisions.length} divisions</p>
              <div className="flex flex-wrap gap-2 mt-5">
                {c.divisions.map((d) => (
                  <Button key={d.id} variant="outline" size="sm" className="rounded-full"
                    data-testid={`division-btn-${c.name.replace(/\s+/g, "-").toLowerCase()}-${d.name}`}
                    onClick={() => openDivision(c, d)}>Division {d.name}</Button>
                ))}
                {c.divisions.length === 0 && <span className="text-xs text-slate-400">No divisions</span>}
              </div>
            </div>
          ))}
        </div>
      </Shell>
    );
  }

  const s = result?.stats;
  return (
    <Shell wide title={`${selClass.name} — Division ${division.name}`} subtitle={overview?.school?.name} nav={
      <Button variant="ghost" size="sm" onClick={() => { setSelClass(null); setDivision(null); setResult(null); }} data-testid="back-to-classes">
        <ArrowLeft className="h-4 w-4 mr-1" />Classes
      </Button>
    }>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <Select value={testId || "__all"} onValueChange={(v) => openDivision(selClass, division, v === "__all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-80" data-testid="teacher-test-filter"><SelectValue placeholder="All tests" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All tests (cumulative)</SelectItem>
            {tests.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button onClick={() => download("csv")} data-testid="download-csv-btn"><Download className="h-4 w-4 mr-1" />Download CSV</Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="download-pdf-btn"><FileText className="h-4 w-4 mr-1" />PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6" data-testid="division-stats">
        <StatCard label="Students" value={s?.total_students ?? 0} testid="div-total" />
        <StatCard label="Completed" value={s?.completed ?? 0} testid="div-completed" />
        <StatCard label="Avg %" value={`${s?.avg_percentage ?? 0}%`} testid="div-avg" />
        <StatCard label="Avg Score" value={s?.avg_score ?? 0} testid="div-avg-score" />
        <StatCard label="Highest" value={`${s?.highest ?? 0}%`} testid="div-high" />
        <StatCard label="Lowest" value={`${s?.lowest ?? 0}%`} testid="div-low" />
      </div>

      <h3 className="text-xl font-head font-semibold mt-10 mb-4">Division CT Competency Averages</h3>
      <CTGrid ct={result?.ct} testid="division-ct-grid" />

      <h3 className="text-xl font-head font-semibold mt-10 mb-4">Student Results (Roll No. ascending)</h3>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <Table data-testid="results-table" className="text-sm">
          <TableHeader>
            <TableRow className="bg-slate-50/70">
              <TableHead className="w-14 whitespace-nowrap">Sr.</TableHead>
              <TableHead className="w-16 whitespace-nowrap">Roll</TableHead>
              <TableHead className="min-w-[200px] whitespace-nowrap">Student</TableHead>
              <TableHead className="w-20 whitespace-nowrap">Score</TableHead>
              <TableHead className="w-20 whitespace-nowrap">%</TableHead>
              <TableHead className="min-w-[130px] whitespace-nowrap">Band</TableHead>
              <TableHead className="min-w-[96px] whitespace-nowrap">Decomp</TableHead>
              <TableHead className="min-w-[96px] whitespace-nowrap">Pattern</TableHead>
              <TableHead className="min-w-[96px] whitespace-nowrap">Abstr</TableHead>
              <TableHead className="min-w-[96px] whitespace-nowrap">Algo</TableHead>
              <TableHead className="min-w-[110px] whitespace-nowrap border-l border-slate-200 text-slate-500 font-normal">Time Taken</TableHead>
              <TableHead className="min-w-[110px] whitespace-nowrap text-slate-500 font-normal">Start Time</TableHead>
              <TableHead className="min-w-[110px] whitespace-nowrap text-slate-500 font-normal">End Time</TableHead>
              <TableHead className="min-w-[120px] whitespace-nowrap text-slate-500 font-normal">Test Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(result?.rows || []).map((r) => (
              <TableRow key={r.student_id} className="cursor-pointer hover:bg-slate-50" data-testid={`student-row-${r.roll_no}`}
                onClick={() => navigate(`/teacher/student/${r.student_id}`)}>
                <TableCell className="text-slate-500">{r.sr}</TableCell>
                <TableCell className="font-medium">{r.roll_no}</TableCell>
                <TableCell className="font-semibold text-slate-900 whitespace-nowrap">{r.name}</TableCell>
                <TableCell className="whitespace-nowrap">{r.total_questions ? `${r.correct}/${r.total_questions}` : "—"}</TableCell>
                <TableCell className="font-semibold whitespace-nowrap">{r.percentage === null ? "—" : `${r.percentage}%`}</TableCell>
                <TableCell><span className={`text-xs font-semibold px-2 py-1 rounded-full border whitespace-nowrap ${bandColor(r.band)}`}>{r.band}</span></TableCell>
                {r.ct.map((c) => (
                  <TableCell key={c.ct_skill}>
                    <span className="inline-block min-w-[54px] text-center px-2 py-1 rounded font-medium"
                      style={{ background: c.percentage === null ? "#F1F5F9" : CT_BG[c.ct_skill] }}>
                      {c.percentage === null ? "N/A" : `${c.percentage}%`}
                    </span>
                  </TableCell>
                ))}
                <TableCell className="whitespace-nowrap text-slate-500 border-l border-slate-200" data-testid={`time-taken-${r.roll_no}`}>{r.attempts === 0 ? "N/A" : fmtDuration(r.duration_seconds)}</TableCell>
                <TableCell className="whitespace-nowrap text-slate-500" data-testid={`start-time-${r.roll_no}`}>{fmtTime(r.started_at)}</TableCell>
                <TableCell className="whitespace-nowrap text-slate-500" data-testid={`end-time-${r.roll_no}`}>{fmtTime(r.submitted_at)}</TableCell>
                <TableCell className="whitespace-nowrap text-slate-500" data-testid={`test-date-${r.roll_no}`}>{fmtDate(r.submitted_at)}</TableCell>
              </TableRow>
            ))}
            {result?.rows?.length === 0 && (
              <TableRow><TableCell colSpan={14} className="text-center text-slate-500 py-8">No students in this division</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Shell>
  );
}
